// ─────────────────────────────────────────────────────────────
// holidays.js — Génération automatique des slots "ponts"
// Utilise l'API gratuite date.nager.at (pas de clé requise)
// ─────────────────────────────────────────────────────────────

// Détecte le code pays depuis le navigateur
export function detectCountry() {
  const lang = navigator.language || 'fr-FR';
  const country = lang.split('-')[1] || 'FR';
  return country.toUpperCase();
}

// Formate une date en "ven. 6 juin" — deux appels séparés pour éviter
// le bug Chrome/Windows qui n'inclut pas le numéro de jour quand weekday est combiné
function formatLabel(date) {
  const wd = date.toLocaleDateString('fr-FR', { weekday:'short' });
  const dm = date.toLocaleDateString('fr-FR', { day:'numeric', month:'long' });
  return `${wd} ${dm}`;
}

// Ajoute N jours à une date
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// Formate en YYYY-MM-DD
function toIso(date) {
  return date.toISOString().split('T')[0];
}

// Récupère les jours fériés depuis l'API nager.at
async function fetchHolidays(countryCode, year) {
  try {
    const res = await fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/${countryCode}`);
    if (!res.ok) return [];
    return await res.json();
  } catch {
    return [];
  }
}

// Génère les slots "ponts" depuis les jours fériés
export async function generateBridgeSlots(countryCode) {
  const today = new Date();
  const in12months = new Date();
  in12months.setMonth(in12months.getMonth() + 12);

  // Récupère les fériés de l'année en cours et de l'année suivante
  const thisYear = today.getFullYear();
  const nextYear = thisYear + 1;

  const [holidays1, holidays2] = await Promise.all([
    fetchHolidays(countryCode, thisYear),
    fetchHolidays(countryCode, nextYear),
  ]);

  const holidays = [...holidays1, ...holidays2];
  const slots = [];

  for (const holiday of holidays) {
    const date = new Date(holiday.date + 'T12:00:00');

    // Ignore les fériés passés ou au-delà de 12 mois
    if (date < today || date > in12months) continue;

    const dow = date.getDay(); // 0=dim, 1=lun, ..., 5=ven, 4=jeu, 2=mar

    let departDate, returnDate, type, nights;

    if (dow === 1) {
      // Lundi férié → vendredi au lundi (3 nuits dont 1 sam → longWE)
      departDate = addDays(date, -3); // vendredi
      returnDate = date;              // lundi
      type = 'longWE';
      nights = 3;
    } else if (dow === 5) {
      // Vendredi férié → jeudi au dimanche (3 nuits dont 1 sam → longWE)
      departDate = addDays(date, -1); // jeudi (veille)
      returnDate = addDays(date, 2);  // dimanche
      type = 'longWE';
      nights = 3;
    } else if (dow === 4) {
      // Jeudi férié (pont) → mercredi au dimanche (4 nuits)
      departDate = addDays(date, -1); // mercredi soir (veille)
      returnDate = addDays(date, 3);  // dimanche soir
      type = 'longWE';
      nights = 4;
    } else if (dow === 2) {
      // Mardi férié (pont) → vendredi précédent au mardi (4 nuits)
      departDate = addDays(date, -4); // vendredi soir
      returnDate = date;              // mardi soir
      type = 'longWE';
      nights = 4;
    } else if (dow === 3) {
      // Mercredi férié → 3 slots
      const configs = [
        { departDate: addDays(date, -5), returnDate: date,             type: 'short', nights: 5 }, // pont amont : ven → mer
        { departDate: addDays(date, -1), returnDate: addDays(date, 4), type: 'short', nights: 5 }, // pont aval  : mar → dim
        { departDate: addDays(date, -5), returnDate: addDays(date, 4), type: 'long',   nights: 9 }, // long séjour: ven → dim
      ];
      for (const cfg of configs) {
        const did = toIso(cfg.departDate);
        const rid = toIso(cfg.returnDate);
        if (slots.find(s => s.departIso === did && s.returnIso === rid)) continue;
        slots.push({
          id:        `bridge_${did}_${rid}`,
          label:     `${formatLabel(cfg.departDate)} — ${formatLabel(cfg.returnDate)}`,
          from:      formatLabel(cfg.departDate),
          to:        formatLabel(cfg.returnDate),
          departIso: did,
          returnIso: rid,
          type:      cfg.type,
          nights:    cfg.nights,
          source:    'auto',
          holiday:   holiday.localName,
        });
      }
      continue;
    } else {
      continue; // Samedi, dimanche → ignoré
    }

    // Évite les doublons (même departDate)
    if (slots.find(s => s.departIso === toIso(departDate))) continue;

    slots.push({
      id:        `bridge_${toIso(departDate)}`,
      label:     `${formatLabel(departDate)} — ${formatLabel(returnDate)}`,
      from:      formatLabel(departDate),
      to:        formatLabel(returnDate),
      departIso: toIso(departDate),
      returnIso: toIso(returnDate),
      type,
      nights,
      source:    'auto', // distingue les slots auto des slots manuels
      holiday:   holiday.localName,
    });
  }

    // Trie par date de départ
  slots.sort((a, b) => a.departIso.localeCompare(b.departIso));

  // Fallback : si aucun slot généré (nager.at vide, pays sans fériés qualifiés…),
  // on produit des créneaux génériques pour que le ranking puisse tourner
  if (slots.length < 3) {
    const start = new Date(today);
    const daysToFri = (5 - start.getDay() + 7) % 7 || 7;
    start.setDate(start.getDate() + daysToFri);
    const fallback = [];
    for (let w = 0; w < 26 && fallback.length < 12; w++) {
      const fri = new Date(start);
      fri.setDate(start.getDate() + w * 7);
      if (fri > in12months) break;
      const mon = addDays(fri, 3);
      const fri2 = addDays(fri, 7);
      const sun2 = addDays(fri, 9);
      const fri3 = addDays(fri, 0);
      const sat2 = addDays(fri, 8);
      fallback.push(
        { id:`we_${toIso(fri)}`,   label:`${formatLabel(fri)} — ${formatLabel(mon)}`,  from:formatLabel(fri),  to:formatLabel(mon),  departIso:toIso(fri),  returnIso:toIso(mon),  type:'longWE', nights:3, source:'auto', holiday:'' },
        { id:`sh_${toIso(fri2)}`,  label:`${formatLabel(fri2)} — ${formatLabel(sun2)}`, from:formatLabel(fri2), to:formatLabel(sun2), departIso:toIso(fri2), returnIso:toIso(sun2), type:'long',   nights:9, source:'auto', holiday:'' },
        { id:`lg_${toIso(fri3)}`,  label:`${formatLabel(fri3)} — ${formatLabel(sat2)}`, from:formatLabel(fri3), to:formatLabel(sat2), departIso:toIso(fri3), returnIso:toIso(sat2), type:'long',   nights:8, source:'auto', holiday:'' },
      );
    }
    // Déduplique et trie
    const seen = new Set(slots.map(s => s.id));
    for (const s of fallback) { if (!seen.has(s.id)) { slots.push(s); seen.add(s.id); } }
    slots.sort((a, b) => a.departIso.localeCompare(b.departIso));
  }

  return slots;
}